/**
 * Markdown as agents write their reports: the common part of CommonMark and GitHub's tables, parsed into blocks and
 * inline pieces the app draws itself (pages/comments/markdown.tsx). No HTML: anything that looks like it is text.
 * Links go only to http(s) and mailto addresses. Items and people named in it (`#[Title](itm_…)`, `@[Name](usr_…)`)
 * are text here, drawn as they are in comments.
 */

export type Inline =
  | { t: "text"; v: string }
  | { t: "code"; v: string }
  | { t: "strong" | "em" | "del"; c: Inline[] }
  | { t: "link"; href: string; c: Inline[] };

export type Align = "left" | "center" | "right" | null;

export type Block =
  | { t: "h"; level: number; c: Inline[] }
  | { t: "p"; c: Inline[] }
  | { t: "code"; lang: string; v: string }
  | { t: "quote"; c: Block[] }
  | { t: "list"; ordered: boolean; start: number; items: Block[][] }
  | { t: "table"; align: Align[]; head: Inline[][]; rows: Inline[][][] }
  | { t: "hr" };

const FENCE = /^ {0,3}(`{3,}|~{3,})\s*([\w+-]*)/u;
const HEADING = /^ {0,3}(#{1,6})(?:\s+(.*?))?\s*#*\s*$/u;
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/u;
const QUOTE = /^ {0,3}> ?/u;
const BULLET = /^( {0,3})([-*+])\s+(.*)$/u;
const NUMBERED = /^( {0,3})(\d{1,9})[.)]\s+(.*)$/u;
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/u;
const BLANK = /^\s*$/u;
const INDENT = /^( {2,}|\t)/u;
const SAFE_URL = /^(https?:\/\/|mailto:)/iu;
/** A token of the app's own (a mention, an item's link): kept as text, drawn as in comments. */
const TOKEN = /^[@#]\[[^\]]*\]\([a-z]{3}_[0-9a-z]+\)/u;
const LINK = /^\[([^\]]*)\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/u;
const AUTOLINK = /^<((?:https?:\/\/|mailto:)[^>\s]+)>/iu;
const BARE_URL = /^https?:\/\/[^\s<]*[^\s<.,;:!?)\]'"]/iu;
const CELL_SPLIT = /(?<!\\)\|/u;
const ITEM_INDENT = /^( {1,4}|\t)/u;
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!|~<>]/u;
const TICKS = /^`+/u;
const EDGE_SPACE = /^\s|\s$/u;

/** A list item's marker, if `line` starts one: its indent, whether it's numbered, its number, and what follows. */
function marker(line: string): { indent: number; ordered: boolean; start: number; rest: string } | null {
  const b = line.match(BULLET);
  if (b) {
    return { indent: (b[1] ?? "").length, ordered: false, start: 1, rest: b[3] ?? "" };
  }
  const n = line.match(NUMBERED);
  return n ? { indent: (n[1] ?? "").length, ordered: true, start: Number(n[2]), rest: n[3] ?? "" } : null;
}

/** Whether a line starts a block of its own (so a paragraph ends before it). */
function starts(line: string): boolean {
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || marker(line) !== null;
}

/** A table row's cells (pipes at the ends optional; `\|` is a pipe in a cell). */
function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) {
    s = s.slice(1);
  }
  if (s.endsWith("|") && !s.endsWith("\\|")) {
    s = s.slice(0, -1);
  }
  return s.split(CELL_SPLIT).map((c) => c.trim().replaceAll("\\|", "|"));
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) {
    return "center";
  }
  if (right) {
    return "right";
  }
  return left ? "left" : null;
}

/** A list starting at `lines[i]`: its items (each parsed as blocks) and where it ends. */
function list(lines: readonly string[], i: number): [Block, number] {
  const first = marker(lines[i] ?? "") as NonNullable<ReturnType<typeof marker>>;
  const items: string[][] = [];
  let at = i;
  while (at < lines.length) {
    const line = lines[at] ?? "";
    const m = marker(line);
    if (m && m.ordered === first.ordered && m.indent <= first.indent + 1) {
      items.push([m.rest]);
    } else if (BLANK.test(line)) {
      // A blank line ends the list unless an item (or its indented text) follows.
      const next = lines[at + 1] ?? "";
      const nm = marker(next);
      if (!(INDENT.test(next) || (nm && nm.ordered === first.ordered))) {
        break;
      }
      items.at(-1)?.push("");
    } else if (INDENT.test(line) || !starts(line)) {
      // Its text going on (indented: a nested list, a second paragraph; or a lazy continuation line).
      items.at(-1)?.push(line.replace(ITEM_INDENT, ""));
    } else {
      break;
    }
    at++;
  }
  return [
    { t: "list", ordered: first.ordered, start: first.start, items: items.map((it) => blocks(it.join("\n"))) },
    at,
  ];
}

/** `text` as blocks. */
export function blocks(text: string): Block[] {
  const lines = text.replaceAll("\r\n", "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (BLANK.test(line)) {
      i++;
      continue;
    }
    const fence = line.match(FENCE);
    if (fence) {
      const close = fence[1] ?? "```";
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith(close)) {
        body.push(lines[i] ?? "");
        i++;
      }
      out.push({ t: "code", lang: fence[2] ?? "", v: body.join("\n") });
      i++;
      continue;
    }
    const heading = line.match(HEADING);
    if (heading) {
      out.push({ t: "h", level: (heading[1] ?? "#").length, c: inline(heading[2] ?? "") });
      i++;
      continue;
    }
    if (RULE.test(line)) {
      out.push({ t: "hr" });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? "")) {
        quoted.push((lines[i] ?? "").replace(QUOTE, ""));
        i++;
      }
      out.push({ t: "quote", c: blocks(quoted.join("\n")) });
      continue;
    }
    if (marker(line)) {
      const [l, next] = list(lines, i);
      out.push(l);
      i = next;
      continue;
    }
    if (line.includes("|") && TABLE_RULE.test(lines[i + 1] ?? "") && (lines[i + 1] ?? "").includes("-")) {
      const head = cells(line);
      const align = cells(lines[i + 1] ?? "").map(alignOf);
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && !BLANK.test(lines[i] ?? "") && (lines[i] ?? "").includes("|")) {
        const row = cells(lines[i] ?? "");
        rows.push(head.map((_, k) => inline(row[k] ?? "")));
        i++;
      }
      out.push({ t: "table", align: head.map((_, k) => align[k] ?? null), head: head.map((h) => inline(h)), rows });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && !BLANK.test(lines[i] ?? "") && (para.length === 0 || !starts(lines[i] ?? ""))) {
      para.push((lines[i] ?? "").trim());
      i++;
    }
    out.push({ t: "p", c: inline(para.join("\n")) });
  }
  return out;
}

/** Emphasis delimiters, longest first. */
const DELIMS: readonly [string, "strong" | "em" | "del"][] = [
  ["**", "strong"],
  ["__", "strong"],
  ["~~", "del"],
  ["*", "em"],
  ["_", "em"],
];

/** Adds `v` to `out`, joining it to the text before. */
function push(out: Inline[], v: string): void {
  const last = out.at(-1);
  if (last?.t === "text") {
    last.v += v;
  } else if (v) {
    out.push({ t: "text", v });
  }
}

const WORD = /\w/u;

/** `s`'s inline pieces: code, emphasis, links (only to web and mail addresses), the rest text. */
export function inline(s: string): Inline[] {
  const out: Inline[] = [];
  let i = 0;
  while (i < s.length) {
    const rest = s.slice(i);
    const ch = s[i] ?? "";
    if (ch === "\\" && i + 1 < s.length && ESCAPABLE.test(s[i + 1] ?? "")) {
      push(out, s[i + 1] ?? "");
      i += 2;
      continue;
    }
    if (ch === "`") {
      const ticks = rest.match(TICKS)?.[0] ?? "`";
      const end = s.indexOf(ticks, i + ticks.length);
      if (end > 0) {
        out.push({ t: "code", v: s.slice(i + ticks.length, end).trim() });
        i = end + ticks.length;
        continue;
      }
    }
    const token = rest.match(TOKEN);
    if (token) {
      push(out, token[0]);
      i += token[0].length;
      continue;
    }
    const link = rest.match(LINK);
    if (link && ch === "[") {
      const href = link[2] ?? "";
      if (SAFE_URL.test(href)) {
        out.push({ t: "link", href, c: inline(link[1] ?? "") });
      } else {
        // A link to anywhere else (a script, a relative path): its words only.
        out.push(...inline(link[1] ?? ""));
      }
      i += link[0].length;
      continue;
    }
    const auto = rest.match(AUTOLINK) ?? (WORD.test(s[i - 1] ?? "") ? null : rest.match(BARE_URL));
    if (auto) {
      const href = auto[1] ?? auto[0];
      out.push({ t: "link", href, c: [{ t: "text", v: href }] });
      i += auto[0].length;
      continue;
    }
    const delim = DELIMS.find(([d]) => rest.startsWith(d));
    // Underscores inside a word (snake_case) are just underscores.
    const inWord = ch === "_" && WORD.test(s[i - 1] ?? "");
    if (delim && !inWord) {
      const [d, t] = delim;
      const end = s.indexOf(d, i + d.length);
      const inner = end > i + d.length ? s.slice(i + d.length, end) : "";
      if (inner && !EDGE_SPACE.test(inner)) {
        out.push({ t, c: inline(inner) });
        i = end + d.length;
        continue;
      }
    }
    push(out, ch);
    i++;
  }
  return out;
}
